// Helpers autour de l'API Office.js : lecture du mail courant
// et persistance des paramètres GLPI dans roamingSettings.

export interface MailContext {
  subject: string;
  senderEmail: string;
  senderName: string;
  /** Corps du mail en texte brut. */
  bodyText: string;
  /** Corps du mail en HTML (Outlook), si disponible. */
  bodyHtml: string;
  /** Internet message id (utile pour log / dédoublonnage). */
  internetMessageId?: string;
  /** Pièces jointes (méta seulement, le contenu se fetch à la demande). */
  attachments: MailAttachmentInfo[];
}

export interface MailAttachmentInfo {
  /** ID Office (utilisé pour getAttachmentContentAsync). */
  id: string;
  name: string;
  contentType: string;
  size: number;
  isInline: boolean;
  /** Identifiant cid pour les images inline (src="cid:…" dans le HTML Outlook). */
  contentId?: string;
}

/** Contenu binaire d'une pièce jointe une fois téléchargée depuis Outlook. */
export interface FetchedAttachment {
  name: string;
  contentType: string;
  size: number;
  /** Données binaires sous forme de Blob, prêt pour FormData. */
  blob: Blob;
}

/** Récupère les informations utiles du mail actuellement ouvert. */
export function getMailContext(): Promise<MailContext> {
  return new Promise((resolve, reject) => {
    const item = Office.context.mailbox.item;
    if (!item) {
      reject(new Error("Aucun mail sélectionné dans Outlook."));
      return;
    }

    const subject = (item.subject as string) || "";
    const senderEmail = item.from?.emailAddress || "";
    const senderName = item.from?.displayName || "";
    const internetMessageId = item.internetMessageId;

    // Liste des pièces jointes (méta uniquement, fetch séparé pour le contenu).
    // Tres defensif : l'API peut etre absente, ou item.attachments peut etre
    // null / non itérable selon la version d'Outlook.
    let attachments: MailAttachmentInfo[] = [];
    try {
      const rawAttachments = (item.attachments as Office.AttachmentDetails[] | undefined) ?? [];
      if (Array.isArray(rawAttachments)) {
        attachments = rawAttachments.map((a) => ({
          id: String(a.id ?? ""),
          name: String(a.name ?? ""),
          contentType: String(a.contentType ?? "application/octet-stream"),
          size: Number(a.size ?? 0),
          isInline: Boolean(a.isInline),
          contentId: String((a as Office.AttachmentDetails & { contentId?: string }).contentId ?? ""),
        }));
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[GLPI Helper] lecture attachments echouee", (e as Error).message);
    }

    Promise.all([
      getMailBodyAsync(item, Office.CoercionType.Text),
      getMailBodyAsync(item, Office.CoercionType.Html).catch(() => ""),
    ])
      .then(([bodyText, bodyHtml]) => {
        resolve({
          subject,
          senderEmail,
          senderName,
          bodyText: bodyText || "",
          bodyHtml: bodyHtml || "",
          internetMessageId,
          attachments,
        });
      })
      .catch((e: Error) => {
        reject(e instanceof Error ? e : new Error(String(e)));
      });
  });
}

function getMailBodyAsync(
  item: Office.MessageRead,
  coercion: Office.CoercionType
): Promise<string> {
  return new Promise((resolve, reject) => {
    item.body.getAsync(coercion, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value || "");
      } else {
        reject(new Error(result.error?.message || "Lecture du corps du mail échouée."));
      }
    });
  });
}

/**
 * Télécharge le contenu binaire d'une pièce jointe Outlook.
 * Office renvoie le contenu en base64 ; on le convertit en Uint8Array puis Blob.
 */
/** Contenu base64 d'une pièce jointe (pour images inline → data: URL dans le HTML). */
export function getAttachmentBase64(
  attachmentId: string,
  meta: MailAttachmentInfo
): Promise<{ base64: string; mime: string }> {
  return new Promise((resolve, reject) => {
    const item = Office.context.mailbox.item;
    if (!item) {
      reject(new Error("Aucun mail sélectionné."));
      return;
    }
    if (typeof item.getAttachmentContentAsync !== "function") {
      reject(new Error("getAttachmentContentAsync non disponible sur cette version d'Outlook."));
      return;
    }
    item.getAttachmentContentAsync(attachmentId, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new Error(result.error?.message || "Téléchargement de la pièce jointe échoué."));
        return;
      }
      const content = result.value;
      const fmt = String(content.format || "").toLowerCase();
      if (fmt !== "base64") {
        reject(
          new Error(
            `Format de pièce jointe non supporté : ${content.format} (attendu : base64).`
          )
        );
        return;
      }
      try {
        const mime = resolveMimeType(meta.name, meta.contentType);
        resolve({ base64: content.content, mime });
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    });
  });
}

export function getAttachmentBlob(
  attachmentId: string,
  meta: MailAttachmentInfo
): Promise<FetchedAttachment> {
  return getAttachmentBase64(attachmentId, meta).then(({ base64, mime }) => {
    const buffer = base64ToArrayBuffer(base64);
    const blob = new Blob([buffer], { type: mime });
    return {
      name: meta.name,
      contentType: meta.contentType,
      size: meta.size,
      blob,
    };
  });
}

function resolveMimeType(filename: string, contentType?: string): string {
  if (contentType && contentType !== "application/octet-stream") {
    return contentType;
  }
  const lower = filename.toLowerCase();
  if (lower.endsWith(".pdf")) return "application/pdf";
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".doc")) return "application/msword";
  if (lower.endsWith(".docx")) {
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  }
  if (lower.endsWith(".xls")) return "application/vnd.ms-excel";
  if (lower.endsWith(".xlsx")) {
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  }
  return contentType || "application/octet-stream";
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const bin = atob(base64);
  const len = bin.length;
  const buffer = new ArrayBuffer(len);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < len; i++) view[i] = bin.charCodeAt(i);
  return buffer;
}

// -------------------------------------------------------------------------
// Roaming settings : persistance par boîte aux lettres / chaîne Office.
// -------------------------------------------------------------------------

const KEY_USER_TOKEN = "glpi_user_token";
const KEY_API_URL = "glpi_api_url";
const KEY_TECH_PROFILE_ID = "glpi_tech_profile_id";
const KEY_MY_USER_ID = "glpi_my_user_id";

function getRoamingSetting<T = string>(key: string): T | undefined {
  try {
    const v = Office.context.roamingSettings.get(key);
    return v as T | undefined;
  } catch {
    return undefined;
  }
}

function setRoamingSetting(key: string, value: string | number | null | undefined): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      if (value === null || value === undefined || value === "") {
        Office.context.roamingSettings.remove(key);
      } else {
        Office.context.roamingSettings.set(key, value);
      }
      Office.context.roamingSettings.saveAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve();
        } else {
          reject(new Error(result.error?.message || "Échec sauvegarde roamingSettings"));
        }
      });
    } catch (e) {
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}

export const settings = {
  getUserToken: (): string | undefined => getRoamingSetting<string>(KEY_USER_TOKEN),
  setUserToken: (v: string | null | undefined): Promise<void> => setRoamingSetting(KEY_USER_TOKEN, v),

  getApiUrl: (): string | undefined => getRoamingSetting<string>(KEY_API_URL),
  setApiUrl: (v: string | null | undefined): Promise<void> => setRoamingSetting(KEY_API_URL, v),

  getTechProfileId: (): number | undefined => {
    const v = getRoamingSetting<number | string>(KEY_TECH_PROFILE_ID);
    if (v === undefined) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  },
  setTechProfileId: (v: number | null | undefined): Promise<void> =>
    setRoamingSetting(KEY_TECH_PROFILE_ID, v ?? null),

  getMyUserId: (): number | undefined => {
    const v = getRoamingSetting<number | string>(KEY_MY_USER_ID);
    if (v === undefined) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  },
  setMyUserId: (v: number | null | undefined): Promise<void> => setRoamingSetting(KEY_MY_USER_ID, v ?? null),
};

// -------------------------------------------------------------------------
// Configuration "build-time" via dotenv-webpack
// -------------------------------------------------------------------------

/**
 * Renvoie l'URL de l'API GLPI, en privilégiant le paramètre utilisateur stocké
 * dans roamingSettings, sinon la valeur compilée via .env.
 */
export function resolveApiUrl(): string {
  const stored = settings.getApiUrl();
  if (stored) return stored;
  return process.env.GLPI_API_URL || "";
}

export function resolveAppToken(): string {
  return process.env.GLPI_APP_TOKEN || "";
}

export function resolveTechProfileId(): number {
  const stored = settings.getTechProfileId();
  if (stored !== undefined) return stored;
  const fromEnv = Number(process.env.GLPI_TECH_PROFILE_ID);
  return Number.isFinite(fromEnv) && fromEnv > 0 ? fromEnv : 6;
}

/**
 * Renvoie la liste des IDs de profils dont les utilisateurs sont éligibles comme
 * techniciens. Lit GLPI_TECH_PROFILE_IDS (séparé par virgule) en priorité,
 * sinon retombe sur GLPI_TECH_PROFILE_ID (legacy).
 *
 * Exemple .env : GLPI_TECH_PROFILE_IDS=4,6  → Super-Admin + Technicien
 */
export function resolveTechProfileIds(): number[] {
  const multi = process.env.GLPI_TECH_PROFILE_IDS;
  if (multi) {
    const ids = multi
      .split(",")
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (ids.length > 0) return ids;
  }
  return [resolveTechProfileId()];
}
